import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  InputAdornment, 
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Skeleton,
  Fade
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  Search as SearchIcon, 
  ViewList as ListIcon, 
  ViewModule as GridIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  WebStories as WebStoriesIcon,
  Edit as EditIcon,
  PostAdd as PostIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import TemplateCard from '../components/TemplateCard';
import TemplateErrorBoundary from '../components/templates/TemplateErrorBoundary';
import { useTracking } from '../hooks/useTracking';
import { debounce } from '../utils/performanceUtils';
import { useTemplateAnalytics } from '../utils/templateAnalytics';
// import { TemplatePerformanceIntegration } from '../utils/templatePerformanceIntegration'; // DISABLED

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
`;

const TemplatesContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  position: 'relative',
  
  // Add a subtle gradient background
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  textAlign: 'center',
  padding: theme.spacing(3, 0),
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '2px',
  },
  
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
    textAlign: 'left',
    padding: theme.spacing(2, 0),
    
    '&::after': {
      left: 0,
      transform: 'none',
      width: '40px',
    }
  }
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}20, transparent)`,
  },
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    '& > *': {
      width: '100%'
    }
  }
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  maxWidth: 400,
  position: 'relative',
  
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: theme.spacing(1.5),
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      },
      
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-1px)',
      }
    }
  },
  
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'none'
  }
}));

const ViewToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '& .MuiButton-root': {
    minWidth: 80,
    transition: 'all 0.2s ease-in-out',
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(5px)',
    
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-1px)',
    },
    
    '&.Mui-selected, &[aria-pressed="true"]': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
      transform: 'translateY(-2px)',
    }
  }
}));

const TemplatesGrid = styled(Grid)(({ theme }) => ({
  '& .MuiGrid-item': {
    display: 'flex',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: `${fadeInUp} 0.6s ease-out`,
    '& > *': {
      width: '100%',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      zIndex: 1,
    }
  },
  
  // Staggered animation for grid items
  '& .MuiGrid-item:nth-of-type(1)': { animationDelay: '0.1s' },
  '& .MuiGrid-item:nth-of-type(2)': { animationDelay: '0.2s' },
  '& .MuiGrid-item:nth-of-type(3)': { animationDelay: '0.3s' },
  '& .MuiGrid-item:nth-of-type(4)': { animationDelay: '0.4s' },
  '& .MuiGrid-item:nth-of-type(5)': { animationDelay: '0.5s' },
  '& .MuiGrid-item:nth-of-type(6)': { animationDelay: '0.6s' },
  '& .MuiGrid-item:nth-of-type(7)': { animationDelay: '0.7s' },
  '& .MuiGrid-item:nth-of-type(8)': { animationDelay: '0.8s' },
  
  // Responsive spacing
  [theme.breakpoints.down('sm')]: {
    '& .MuiGrid-item': {
      marginBottom: theme.spacing(2),
    }
  }
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.02)',
  backdropFilter: 'blur(5px)',
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  }
}));

const AccessibleButton = styled(Button)(({ theme }) => ({
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}));

/**
 * Custom hook to fetch and manage templates with pagination, caching, and performance optimization
 */
const useTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { trackEvent } = useTracking();
  const analytics = useTemplateAnalytics();
  const abortControllerRef = useRef(null);
  const cacheRef = useRef(new Map()); // Simple in-memory cache
  
  // Performance optimization: reduce page size on mobile
  const isMobile = useMediaQuery('(max-width: 600px)');
  const pageSize = isMobile ? 12 : 24;

  const fetchTemplates = useCallback(async (pageNum = 1, isRetry = false, resetCache = false) => {
    const startTime = performance.now();
    
    try {
      const isFirstPage = pageNum === 1;
      const isLoadingMore = pageNum > 1;
      
      if (isFirstPage) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      
      // Check cache first for non-first pages and non-retries
      const cacheKey = `templates_page_${pageNum}_${pageSize}`;
      if (!isRetry && !resetCache && cacheRef.current.has(cacheKey)) {
        const cachedData = cacheRef.current.get(cacheKey);
        const results = Array.isArray(cachedData.results) ? cachedData.results : [];

        console.log("results", results)
        
        if (isFirstPage) {
          setTemplates(results);
          setTotalCount(cachedData.count);
          setHasMore(cachedData.next !== null);
        } else {
          setTemplates(prev => [...prev, ...results]);
          setHasMore(cachedData.next !== null);
        }
        
        // Track cache hit with analytics
        analytics.trackPerformance('template_gallery_load', 0, {
          template_count: results.length,
          total_templates: cachedData.count,
          page: pageNum,
          page_size: pageSize,
          is_retry: isRetry,
          is_loading_more: !isFirstPage,
          cache_hit: true
        });
        
        // Track each cached template load
        results.forEach((template, index) => {
          analytics.trackTemplateLoad(template.id, 0, {
            template_name: template.name,
            template_type: template.is_video ? 'video' : 'image',
            position: index + 1,
            fromCache: true
          });
        });
        
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      const url = new URL(`${process.env.REACT_APP_API_BASE_URL}templates/`);
      url.searchParams.set('page', pageNum.toString());
      url.searchParams.set('page_size', pageSize.toString());
      url.searchParams.set('ordering', '-updated_at'); // Latest first
      
      // Track template loading start
      analytics.trackAction('template_load_start', null, {
        page: pageNum,
        page_size: pageSize,
        is_mobile: isMobile,
        is_retry: isRetry,
        cache_reset: resetCache
      });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch templates`);
      }

      const data = await response.json();
      const results = Array.isArray(data) ? data : [];

      console.log("data", data)
      
      // Cache the response
      cacheRef.current.set(cacheKey, data);
      
      // Limit cache size to prevent memory issues
      if (cacheRef.current.size > 10) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }
      
      // Update state
      if (isFirstPage) {
        setTemplates(results);
      } else {
        setTemplates(prev => [...prev, ...results]);
      }
      
      setTotalCount(data.count || 0);
      setHasMore(data.next !== null);
      setRetryCount(0);
      setPage(pageNum);
      
      // Track successful load
      const loadTime = performance.now() - startTime;
      
      // Use analytics for comprehensive tracking
      analytics.trackPerformance('template_gallery_load', loadTime, {
        template_count: results.length,
        total_templates: data.count,
        page: pageNum,
        page_size: pageSize,
        is_retry: isRetry,
        is_loading_more: isLoadingMore,
        cache_hit: false
      });
      
      // Track each template load
      results.forEach((template, index) => {
        analytics.trackTemplateLoad(template.id, loadTime / results.length, {
          template_name: template.name,
          template_type: template.is_video ? 'video' : 'image',
          position: index + 1,
          fromCache: false
        });
      });
      
      // Legacy tracking for compatibility
      trackEvent('templates_loaded', {
        template_count: results.length,
        total_templates: data.count,
        page: pageNum,
        page_size: pageSize,
        load_time_ms: Math.round(loadTime),
        is_retry: isRetry,
        is_loading_more: isLoadingMore,
        cache_hit: false
      });
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        return;
      }
      
      console.error('Error fetching templates:', error);
      setError(error.message);
      
      // Track error with analytics
      const loadTime = performance.now() - startTime;
      analytics.trackError(error, {
        operation: 'template_load',
        page: pageNum,
        retry_count: retryCount,
        is_retry: isRetry,
        duration: loadTime,
        page_size: pageSize,
        is_mobile: isMobile
      });
      
      // Legacy error tracking
      trackEvent('templates_load_error', {
        error_message: error.message,
        retry_count: retryCount,
        page: pageNum,
        is_retry: isRetry
      });
      
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [retryCount, trackEvent, analytics, pageSize, isMobile]);

  const loadMoreTemplates = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchTemplates(page + 1);
    }
  }, [fetchTemplates, page, loadingMore, hasMore]);

  const retryFetch = useCallback(() => {
    setRetryCount(prev => prev + 1);
    fetchTemplates(1, true, true); // Reset cache on retry
  }, [fetchTemplates]);

  const refreshTemplates = useCallback(() => {
    cacheRef.current.clear(); // Clear cache
    setPage(1);
    setHasMore(true);
    fetchTemplates(1, false, true);
  }, [fetchTemplates]);

  useEffect(() => {
    fetchTemplates(1);
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    loadingMore,
    error,
    retryCount,
    hasMore,
    totalCount,
    page,
    refreshTemplates,
    loadMoreTemplates,
    retryFetch
  };
};

/**
 * Enhanced Templates Page with accessibility and performance optimizations
 * 
 * Features:
 * - WCAG 2.1 AA compliance
 * - Keyboard navigation support
 * - Screen reader compatibility
 * - Performance optimizations
 * - Comprehensive error handling
 * - Analytics tracking
 * 
 * @component
 */
const TemplatesPage = () => {
  const { 
    templates, 
    loading, 
    loadingMore, 
    error, 
    retryCount, 
    hasMore, 
    totalCount,
    page,
    refreshTemplates, 
    loadMoreTemplates,
    retryFetch 
  } = useTemplates();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [typeFilter, setTypeFilter] = useState('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down('sm') || '(max-width: 600px)');
  const { trackEvent } = useTracking();
  const analytics = useTemplateAnalytics();
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  // const performanceGalleryRef = useRef(null); // DISABLED

  // Debounced search to improve performance
  const debouncedSearch = useCallback(
    debounce((query) => {
      const startTime = performance.now();
      setDebouncedSearchQuery(query);
      
      if (query.trim()) {
        // Enhanced search tracking with analytics
        analytics.trackSearch(query, templates.length, performance.now() - startTime, {
          searchType: 'instant',
          filters: { type: typeFilter },
          view_mode: viewMode,
          is_mobile: isMobile
        });
        
        // Legacy tracking
        trackEvent('template_search', {
          search_query: query,
          query_length: query.length
        });
      }
    }, 300),
    [trackEvent, analytics, templates.length, typeFilter, viewMode, isMobile]
  );

  // Initialize analytics session
  useEffect(() => {
    const sessionStartTime = performance.now();
    
    analytics.trackAction('template_page_loaded', null, {
      initial_view_mode: viewMode,
      is_mobile: isMobile,
      page_load_time: sessionStartTime,
      user_agent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Cleanup and session tracking on unmount
    return () => {
      const sessionDuration = performance.now() - sessionStartTime;
      
      analytics.trackEngagement('template_page_session_end', sessionDuration, {
        session_duration: sessionDuration,
        analytics_summary: analytics.getSummary()
      });
    };
  }, []); // Only run once on mount

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  // Enhanced gallery initialization with analytics
  useEffect(() => {
    if (containerRef.current && !loading && !error && templates.length > 0) {
      // Enhanced gallery analytics tracking
      analytics.trackAction('template_gallery_displayed', null, {
        template_count: templates.length,
        total_templates: totalCount,
        current_page: page,
        view_mode: viewMode,
        is_mobile: isMobile,
        search_query: searchQuery || null,
        type_filter: typeFilter,
        performance_integration: false,
        session_summary: analytics.getSummary()
      });
      
      // Legacy tracking for compatibility
      trackEvent('template_gallery_loaded', {
        template_count: templates.length,
        is_mobile: isMobile,
        view_mode: viewMode,
        performance_integration: false
      });
    }
  }, [templates, loading, error, trackEvent, analytics, viewMode, isMobile, totalCount, page, searchQuery, typeFilter]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    // Enhanced analytics tracking
    analytics.trackAction('view_mode_changed', null, {
      old_mode: viewMode,
      new_mode: mode,
      template_count: templates.length,
      is_mobile: isMobile
    });
    
    trackEvent('template_view_mode_changed', { view_mode: mode });
  };

  const handleTypeFilterChange = (event) => {
    const value = event.target.value;
    setTypeFilter(value);
    
    // Enhanced analytics tracking
    analytics.trackAction('filter_changed', null, {
      filter_type: 'type',
      old_value: typeFilter,
      new_value: value,
      template_count: templates.length,
      search_query: searchQuery || null
    });
    
    trackEvent('template_filter_changed', { filter_type: value });
  };

  const handleRefresh = () => {
    const startTime = performance.now();
    refreshTemplates();
    setSnackbarMessage('Templates refreshed');
    setSnackbarOpen(true);
    
    // Track refresh action
    analytics.trackAction('templates_refreshed', null, {
      template_count: templates.length,
      page: page,
      search_query: searchQuery || null,
      refresh_time: performance.now() - startTime
    });
    
    trackEvent('templates_refreshed');
  };

  const handleLoadMore = () => {
    // Track load more engagement
    analytics.trackEngagement('load_more_clicked', null, {
      current_page: page,
      current_template_count: templates.length,
      total_templates: totalCount,
      search_query: searchQuery || null,
      view_mode: viewMode,
      is_mobile: isMobile
    });
    
    // Call the actual load more function
    loadMoreTemplates();
  };

  const handleKeyDown = (event) => {
    // Enhanced keyboard shortcuts for accessibility
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'f':
          event.preventDefault();
          searchInputRef.current?.focus();
          
          // Track keyboard shortcut usage
          analytics.trackAction('keyboard_shortcut', null, {
            shortcut: 'focus_search',
            key_combination: 'Ctrl+F'
          });
          break;
        case 'r':
          event.preventDefault();
          handleRefresh();
          
          analytics.trackAction('keyboard_shortcut', null, {
            shortcut: 'refresh',
            key_combination: 'Ctrl+R'
          });
          break;
        case 'n':
          event.preventDefault();
          // Navigate to new template creation
          window.location.href = '/editor';
          
          analytics.trackAction('keyboard_shortcut', null, {
            shortcut: 'new_template',
            key_combination: 'Ctrl+N'
          });
          break;
        default:
          break;
      }
    }
    
    // Arrow key navigation for template grid
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || 
        event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const focusedElement = document.activeElement;
      const templateCards = Array.from(document.querySelectorAll('[data-testid="template-card"]'));
      const currentIndex = templateCards.findIndex(card => 
        card.contains(focusedElement) || card === focusedElement
      );
      
      if (currentIndex >= 0) {
        let nextIndex = currentIndex;
        const isMobile = window.innerWidth <= 600;
        const columnsPerRow = isMobile ? 1 : window.innerWidth <= 960 ? 2 : window.innerWidth <= 1280 ? 3 : 4;
        
        switch (event.key) {
          case 'ArrowRight':
            nextIndex = Math.min(currentIndex + 1, templateCards.length - 1);
            break;
          case 'ArrowLeft':
            nextIndex = Math.max(currentIndex - 1, 0);
            break;
          case 'ArrowDown':
            nextIndex = Math.min(currentIndex + columnsPerRow, templateCards.length - 1);
            break;
          case 'ArrowUp':
            nextIndex = Math.max(currentIndex - columnsPerRow, 0);
            break;
          default:
            // No action needed for other keys
            break;
        }
        
        if (nextIndex !== currentIndex) {
          event.preventDefault();
          const nextCard = templateCards[nextIndex];
          const focusableElement = nextCard.querySelector('button, a, [tabindex="0"]') || nextCard;
          focusableElement.focus();
        }
      }
    }
    
    // Enter key for load more
    if (event.key === 'Enter' && hasMore && document.activeElement?.getAttribute('aria-label')?.includes('Load more')) {
      event.preventDefault();
      loadMoreTemplates();
    }
  };

  const getFilteredTemplates = () => {
    if (!templates || !Array.isArray(templates)) return [];
    
    return templates.filter(template => {
      const matchesSearch = 
        !debouncedSearchQuery ||
        template.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        template.category?.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      
      const matchesType = 
        typeFilter === 'all' || 
        (typeFilter === 'image' && !template.is_video) ||
        (typeFilter === 'video' && template.is_video);
      
      return matchesSearch && matchesType;
    });
  };

  const filteredTemplates = getFilteredTemplates();

  // Loading state with skeleton
  if (loading) {
    return (
      <Box sx={{ p: 3, flexGrow: 1 }} onKeyDown={handleKeyDown} tabIndex={-1}>
        <PageHeader>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="80%" height={24} />
        </PageHeader>
        <TemplatesContainer>
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rectangular" height={56} />
          </Box>
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))}
          </Grid>
        </TemplatesContainer>
      </Box>
    );
  }

  // Error state with retry options
  if (error) {
    return (
      <Box sx={{ p: 3, flexGrow: 1 }} onKeyDown={handleKeyDown} tabIndex={-1}>
        <PageHeader>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700
            }}
          >
            Templates
          </Typography>
        </PageHeader>
        <TemplatesContainer>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <AccessibleButton
                color="inherit"
                size="small"
                onClick={retryFetch}
                startIcon={<RefreshIcon />}
                aria-label="Retry loading templates"
              >
                Retry
              </AccessibleButton>
            }
          >
            <Typography variant="h6" component="h2">
              Unable to load templates
            </Typography>
            <Typography variant="body2">
              {error}
              {retryCount > 0 && ` (Attempt ${retryCount + 1})`}
            </Typography>
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Press Ctrl+R (Cmd+R on Mac) to refresh, or click the retry button above.
            </Typography>
          </Box>
        </TemplatesContainer>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, 
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
        position: 'relative',
        
        // Add subtle animated background pattern
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}08 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}08 0%, transparent 50%)`,
          pointerEvents: 'none',
        }
      }} 
      onKeyDown={handleKeyDown} 
      tabIndex={-1}
    >
      {/* Enhanced Page Header */}
      <PageHeader>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Social Templates
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Create and manage your social media templates for consistent branding. 
            {templates.length > 0 && (
              <> You have {templates.length} template{templates.length !== 1 ? 's' : ''} available.</>
            )}
          </Typography>
        </Box>
      </PageHeader>
      
      <TemplatesContainer>
        <ControlsContainer>
          <SearchContainer>
            <TextField
              fullWidth
              placeholder="Search templates... (Ctrl+F)"
              value={searchQuery}
              onChange={handleSearchChange}
              size={isMobile ? "medium" : "small"}
              inputRef={searchInputRef}
              inputProps={{
                'aria-label': 'Search templates',
                'aria-describedby': 'search-help'
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
            />
            <Typography 
              id="search-help" 
              variant="caption" 
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Search by name, description, or category
            </Typography>
          </SearchContainer>

          <FormControl size={isMobile ? "medium" : "small"} sx={{ minWidth: 140 }}>
            <InputLabel id="type-filter-label">Type</InputLabel>
            <Select
              labelId="type-filter-label"
              value={typeFilter}
              onChange={handleTypeFilterChange}
              label="Type"
              aria-describedby="type-filter-help"
            >
              <MenuItem value="all">All Templates</MenuItem>
              <MenuItem value="image">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon fontSize="small" aria-hidden="true" />
                  Image Templates
                </Box>
              </MenuItem>
              <MenuItem value="video">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VideoIcon fontSize="small" aria-hidden="true" />
                  Video Templates
                </Box>
              </MenuItem>
            </Select>
            <Typography 
              id="type-filter-help" 
              variant="caption" 
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              Filter by template type
            </Typography>
          </FormControl>

          {!isMobile && (
            <ViewToggleContainer role="group" aria-label="View mode selection">
              <AccessibleButton
                variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                onClick={() => handleViewModeChange('grid')}
                startIcon={<GridIcon />}
                size="small"
                aria-pressed={viewMode === 'grid'}
                aria-label="Grid view"
              >
                Grid
              </AccessibleButton>
              <AccessibleButton
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => handleViewModeChange('list')}
                startIcon={<ListIcon />}
                size="small"
                aria-pressed={viewMode === 'list'}
                aria-label="List view"
              >
                List
              </AccessibleButton>
            </ViewToggleContainer>
          )}

          <AccessibleButton
            variant="outlined"
            onClick={handleRefresh}
            size={isMobile ? "medium" : "small"}
            startIcon={<RefreshIcon />}
            aria-label="Refresh templates (Ctrl+R)"
          >
            Refresh
          </AccessibleButton>

          <AccessibleButton
            variant="contained"
            component={Link}
            to="/editor"
            size={isMobile ? "medium" : "small"}
            startIcon={<WebStoriesIcon />}
            aria-label="Create new template"
          >
            New Template
          </AccessibleButton>
        </ControlsContainer>

        {filteredTemplates.length === 0 ? (
          <Fade in timeout={300}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              textAlign="center"
              role="region"
              aria-label="No templates found"
            >
              <FilterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} aria-hidden="true" />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {debouncedSearchQuery || typeFilter !== 'all' 
                  ? 'No templates match your filters' 
                  : 'No templates available'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {debouncedSearchQuery || typeFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first template to get started'}
              </Typography>
              {(!debouncedSearchQuery && typeFilter === 'all') && (
                <AccessibleButton
                  variant="contained"
                  component={Link}
                  to="/editor"
                  sx={{ mt: 2 }}
                  startIcon={<WebStoriesIcon />}
                  aria-label="Create your first template"
                >
                  Create First Template
                </AccessibleButton>
              )}
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={300}>
            <Box>
              <StatsContainer>
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    role="status"
                    aria-live="polite"
                  >
                    Showing {filteredTemplates.length} of {totalCount} templates
                    {page > 1 && ` (Page ${page})`}
                  </Typography>
                  {totalCount > templates.length && (
                    <Typography 
                      variant="caption" 
                      color="text.disabled"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      Loaded {templates.length} of {totalCount} total
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Performance indicator for development */}
                  {process.env.NODE_ENV === 'development' && (
                    <Typography 
                      variant="caption" 
                      color="text.disabled"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {isMobile ? 'Mobile' : 'Desktop'} • Paginated
                    </Typography>
                  )}
                </Box>
              </StatsContainer>
              
              {viewMode === 'grid' ? (
                <Box>
                  <TemplatesGrid 
                    container 
                    spacing={3} 
                    role="grid" 
                    aria-label="Template grid"
                    data-testid="templates-grid"
                    ref={containerRef}
                    sx={{
                      // Enhanced grid styling
                      '& .MuiGrid-item': {
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                        }
                      }
                    }}
                  >
                    {filteredTemplates.map((template, index) => (
                      <Grid 
                        key={template.id || index} 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        lg={3} 
                        role="gridcell"
                        data-template-id={template.id}
                        data-testid="template-card"
                        sx={{
                          // Touch-friendly spacing on mobile
                          minHeight: isMobile ? '44px' : 'auto'
                        }}
                      >
                        <TemplateCard 
                          template={template} 
                          isMobile={isMobile}
                          onTemplateLoaded={(templateData) => {
                            // Enhanced template card analytics
                            analytics.trackAction('template_card_loaded', template.id, {
                              template_name: template.name,
                              template_type: template.is_video ? 'video' : 'image',
                              load_order: index + 1,
                              is_mobile: isMobile,
                              page: page,
                              search_query: searchQuery || null,
                              view_mode: viewMode
                            });
                            
                            // Legacy tracking for compatibility
                            trackEvent('template_card_loaded', {
                              template_id: template.id,
                              load_order: index + 1,
                              is_mobile: isMobile
                            });
                          }}
                        />
                      </Grid>
                    ))}
                  </TemplatesGrid>
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <AccessibleButton
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        size="large"
                        startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                        aria-label={loadingMore ? "Loading more templates" : "Load more templates"}
                        sx={{
                          minWidth: 200,
                          height: 48,
                          borderRadius: 3,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '10',
                            transform: 'translateY(-1px)',
                          }
                        }}
                      >
                        {loadingMore ? 'Loading...' : `Load More Templates`}
                      </AccessibleButton>
                    </Box>
                  )}
                  
                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading more templates...
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box 
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  role="list"
                  aria-label="Template list"
                >
                  {filteredTemplates.map((template, index) => (
                    <Paper
                      key={template.id || index}
                      elevation={1}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        '&:hover': {
                          backgroundColor: 'grey.50',
                          cursor: 'pointer'
                        },
                        '&:focus-within': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2
                        }
                      }}
                      role="listitem"
                    >
                      {/* Template Preview */}
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                        aria-hidden="true"
                      >
                        {template.preview_img ? (
                          <img
                            src={template.preview_img}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          template.is_video ? (
                            <VideoIcon color="action" />
                          ) : (
                            <ImageIcon color="action" />
                          )
                        )}
                      </Box>

                      {/* Template Info */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5
                        }}>
                          <Typography variant="h6" component="h3" noWrap>
                            {template.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={template.is_video ? 'Video' : 'Image'}
                            color={template.is_video ? 'primary' : 'secondary'}
                            icon={template.is_video ? <VideoIcon /> : <ImageIcon />}
                            aria-label={`${template.is_video ? 'Video' : 'Image'} template`}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          Created {new Date(template.created_at || template.updated_at).toLocaleDateString()}
                        </Typography>
                      </Box>

                      {/* Template Actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Edit Template">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/editor/${template.id}`}
                            aria-label={`Edit ${template.name} template`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Use Template">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/post/create?template=${template.id}`}
                            aria-label={`Use ${template.name} template to create post`}
                          >
                            <PostIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </TemplatesContainer>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        aria-describedby="snackbar-message"
      />
    </Box>
  );
};

/**
 * Wrapped Templates Page with Error Boundary
 */
const TemplatesPageWithErrorBoundary = () => (
  <TemplateErrorBoundary>
    <TemplatesPage />
  </TemplateErrorBoundary>
);

export default TemplatesPageWithErrorBoundary; 